// FindingsPanel — slide-in drawer for tool invocation results.
//
// Single rendering surface for any tool that emits `Finding[]` —
// sidecar lint, report-gen grade, future LLM checks. One UI to learn;
// every tool's analytical output flows through here (FR 2026-05-31).
//
// v1 ships description-only fix display (no "Apply fix" button — that
// needs its own patch format + preview + capability story, deferred).
// Re-run button per source calls the parent's onRerun callback; the
// parent owns the actual proxy invocation.
//
// Mockable: when no real result has landed yet, the parent can pass
// a `data` prop sourced from fixtures (lib/findings-fixtures.ts) to
// iterate on the UX before the proxy endpoint is wired in.

import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, Info, RefreshCw, X } from 'lucide-react';
import type { Finding, ToolInvokeResult } from '@captain-tasko/shared';

export type FindingSeverity = Finding['severity'];

export interface FindingsPanelData {
  /** The tool action that produced this set, e.g. 'report-gen:grade-website'.
   *  Used as the panel title + the re-run handle. */
  actionKey: string;
  /** Operator-readable label for the action — falls back to actionKey. */
  actionLabel?: string;
  /** The raw ToolInvokeResult. The component renders result.findings; meta
   *  is reserved for tool-specific banners. */
  result: ToolInvokeResult;
}

export interface FindingsPanelProps {
  /** When null, the panel is closed. */
  data: FindingsPanelData | null;
  /** Close handler — usually setState back to null. */
  onClose: () => void;
  /** Optional re-run handler. When omitted, the Re-run button is hidden.
   *  Receives the actionKey so the parent can fire the same tool again. */
  onRerun?: (actionKey: string) => void;
  /** True while a re-run is in flight (parent owns the loading state). */
  rerunning?: boolean;
}

export function FindingsPanel({ data, onClose, onRerun, rerunning }: FindingsPanelProps) {
  const [severityFilter, setSeverityFilter] = useState<Set<FindingSeverity>>(
    () => new Set(['info', 'warn', 'error']),
  );

  // Reset the severity filter every time a new result lands so the
  // operator always sees the full set on a fresh run.
  useEffect(() => {
    setSeverityFilter(new Set(['info', 'warn', 'error']));
  }, [data?.result.runId]);

  // Close on Escape.
  useEffect(() => {
    if (!data) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [data, onClose]);

  const findings = data?.result.findings ?? [];

  const counts = useMemo(() => {
    const out: Record<FindingSeverity, number> = { info: 0, warn: 0, error: 0 };
    for (const f of findings) out[f.severity] = (out[f.severity] ?? 0) + 1;
    return out;
  }, [findings]);

  const filtered = useMemo(
    () => findings.filter((f) => severityFilter.has(f.severity)),
    [findings, severityFilter],
  );

  // Group by source so the panel reads "all of source X here, source Y
  // below" instead of a flat list of mixed origins.
  const grouped = useMemo(() => {
    const m = new Map<string, Finding[]>();
    for (const f of filtered) {
      const list = m.get(f.source) ?? [];
      list.push(f);
      m.set(f.source, list);
    }
    return Array.from(m.entries());
  }, [filtered]);

  if (!data) return null;

  function toggleSeverity(s: FindingSeverity) {
    setSeverityFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      // Never go fully empty — that's confusing; rebind back to all.
      if (next.size === 0) return new Set(['info', 'warn', 'error']);
      return next;
    });
  }

  return (
    <>
      {/* Dim backdrop. Click closes. */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 60,
          background: 'rgba(0, 0, 0, 0.32)',
        }}
        aria-hidden
      />
      {/* Drawer. Fixed right edge; ~440px wide; full height; subtle
          shadow. Click events inside don't bubble to the backdrop. */}
      <aside
        role="dialog"
        aria-label={`Findings — ${data.actionLabel ?? data.actionKey}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          zIndex: 70,
          width: 440, maxWidth: '95vw',
          background: 'var(--surface-raised)',
          borderLeft: '1px solid var(--border-subtle)',
          boxShadow: '-8px 0 24px rgba(0, 0, 0, 0.18)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <header
          style={{
            display: 'flex', alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '14px 16px',
            borderBottom: '1px solid var(--border-subtle)',
            gap: 10,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
              {data.actionLabel ?? data.actionKey}
            </h2>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {findings.length} finding{findings.length === 1 ? '' : 's'}
              {' · '}runId{' '}
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                {data.result.runId.slice(0, 8)}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {onRerun && (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => onRerun(data.actionKey)}
                disabled={rerunning}
                title="Re-run this action against the same document"
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                <RefreshCw size={13} aria-hidden style={{ marginRight: 4 }} />
                {rerunning ? 'Running…' : 'Re-run'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close findings panel"
              title="Close"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, padding: 0,
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)',
              }}
            >
              <X size={16} aria-hidden />
            </button>
          </div>
        </header>

        {/* Severity filter chips */}
        <div
          style={{
            display: 'flex', gap: 6,
            padding: '10px 16px',
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--surface-canvas)',
          }}
        >
          <SeverityChip
            severity="error"
            count={counts.error}
            active={severityFilter.has('error')}
            onClick={() => toggleSeverity('error')}
          />
          <SeverityChip
            severity="warn"
            count={counts.warn}
            active={severityFilter.has('warn')}
            onClick={() => toggleSeverity('warn')}
          />
          <SeverityChip
            severity="info"
            count={counts.info}
            active={severityFilter.has('info')}
            onClick={() => toggleSeverity('info')}
          />
        </div>

        {/* Findings list — grouped by source */}
        <div
          style={{
            flex: 1, overflowY: 'auto',
            padding: '8px 0',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              {findings.length === 0
                ? 'No findings returned.'
                : 'No findings match the current severity filter.'}
            </div>
          ) : (
            grouped.map(([source, list]) => (
              <section key={source}>
                <h3
                  style={{
                    margin: '8px 16px 4px',
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--text-muted)',
                  }}
                >
                  {source} · {list.length}
                </h3>
                {list.map((f, i) => (
                  <FindingRow key={`${source}-${i}`} finding={f} />
                ))}
              </section>
            ))
          )}
        </div>
      </aside>
    </>
  );
}

// ── chip + row helpers ───────────────────────────────────────────

function SeverityChip({
  severity, count, active, onClick,
}: {
  severity: FindingSeverity;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  const colors = severityColors(severity);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={`Toggle ${severity} findings`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 9px',
        height: 24,
        borderRadius: 999,
        border: `1px solid ${active ? colors.border : 'var(--border-subtle)'}`,
        background: active ? colors.bg : 'var(--surface-raised)',
        color: active ? colors.fg : 'var(--text-muted)',
        opacity: active ? 1 : 0.55,
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {colors.icon}
      <span style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>{severity}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</span>
    </button>
  );
}

function FindingRow({ finding }: { finding: Finding }) {
  const colors = severityColors(finding.severity);
  return (
    <article
      style={{
        margin: '6px 12px',
        padding: '10px 12px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface-canvas)',
        border: `1px solid var(--border-subtle)`,
        borderLeft: `3px solid ${colors.accent}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span style={{ marginTop: 1, color: colors.accent, flexShrink: 0 }}>{colors.icon}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-secondary)' }}>
              {finding.rule}
            </span>
            {(finding.file || typeof finding.line === 'number') && (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>
                {finding.file ?? '—'}
                {typeof finding.line === 'number' ? `:${finding.line}` : ''}
                {typeof finding.column === 'number' ? `:${finding.column}` : ''}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 3, lineHeight: 1.45 }}>
            {finding.msg}
          </div>
          {finding.fix?.description && (
            <div
              style={{
                marginTop: 6,
                padding: '6px 8px',
                background: 'color-mix(in srgb, var(--accent-primary) 8%, transparent)',
                border: '1px dashed color-mix(in srgb, var(--accent-primary) 40%, transparent)',
                borderRadius: 'var(--radius-sm, 4px)',
                fontSize: 12,
                color: 'var(--text-secondary)',
              }}
            >
              <strong style={{ color: 'var(--accent-primary)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 6 }}>
                Suggested fix
              </strong>
              {finding.fix.description}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

// ── severity color/icon lookup ──────────────────────────────────

function severityColors(severity: FindingSeverity): {
  fg: string; bg: string; border: string; accent: string; icon: JSX.Element;
} {
  switch (severity) {
    case 'error':
      return {
        fg: 'var(--danger-primary)',
        bg: 'color-mix(in srgb, var(--danger-primary) 14%, transparent)',
        border: 'var(--danger-primary)',
        accent: 'var(--danger-primary)',
        icon: <AlertCircle size={12} aria-hidden />,
      };
    case 'warn':
      return {
        fg: 'var(--warning-primary)',
        bg: 'color-mix(in srgb, var(--warning-primary) 14%, transparent)',
        border: 'var(--warning-primary)',
        accent: 'var(--warning-primary)',
        icon: <AlertTriangle size={12} aria-hidden />,
      };
    case 'info':
    default:
      return {
        fg: 'var(--accent-primary)',
        bg: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)',
        border: 'var(--accent-primary)',
        accent: 'var(--accent-primary)',
        icon: <Info size={12} aria-hidden />,
      };
  }
}
